import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Loader2, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BGV_ACTIVE, deleteBgv, listBgv } from "@/lib/bgvApi";
import { safeApiError } from "@/lib/safeErrorText";

function chip(r) {
  if (BGV_ACTIVE.includes(r.status) || r.choir?.status === "running") return ["Working…", "text-amber-200 bg-amber-300/10"];
  if (r.status === "error") return ["Stopped", "text-rose-200 bg-rose-400/10"];
  return r.saved ? ["Saved", "text-emerald-200 bg-emerald-400/10"] : ["Ready", "text-violet-200 bg-violet-400/10"];
}

/** Every set of background singers you've made — open to keep mixing, or remove. */
export default function BgvHistory() {
  const [rows, setRows] = useState(null);
  const [doomed, setDoomed] = useState(null);
  const load = () => listBgv().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const remove = async () => {
    try { await deleteBgv(doomed.id); toast.success("Removed"); load(); }
    catch (e) { toast.error("Couldn't remove", { description: safeApiError(e) }); }
    finally { setDoomed(null); }
  };
  return (
    <section data-testid="bgv-history" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-7">
      <h2 className="font-display text-base font-semibold md:text-lg">Your background singers</h2>
      {rows === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/40" /> : rows.length === 0 ? (
        <p data-testid="bgv-history-empty" className="mt-3 text-[13px] text-white/50">Nothing yet — pick a lead vocal and add your first singers.</p>
      ) : (
        <ul className="mt-2 divide-y divide-white/[0.06]">
          {rows.map((r) => {
            const [label, cls] = chip(r);
            const busy = label === "Working…";
            return (
              <li key={r.id} data-testid={`bgv-history-${r.id}`} className="flex items-center gap-3 py-3">
                <Link to={`/create/background-singers/${r.id}`} data-testid={`bgv-open-${r.id}`} className="group flex min-w-0 flex-1 items-center gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[14px] font-semibold group-hover:text-violet-200">{r.title}</span>
                    <span className="block truncate text-[11px] text-white/45">{new Date(r.created_at).toLocaleDateString()} · {r.source?.label}</span>
                  </span>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${cls}`}>{label}</span>
                  <ChevronRight className="h-4 w-4 text-white/35 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <button type="button" data-testid={`bgv-delete-${r.id}`} aria-label="Remove" onClick={() => setDoomed(r)} disabled={busy}
                  className="grid h-9 w-9 place-items-center rounded-full text-white/40 transition-colors hover:bg-rose-500/15 hover:text-rose-200 disabled:opacity-30">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <AlertDialog open={Boolean(doomed)} onOpenChange={(o) => !o && setDoomed(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0e1c] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{doomed?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">The singers and their working files are removed. A song you saved from them stays in your library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bgv-delete-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="bgv-delete-confirm" onClick={remove} className="bg-rose-500 text-white hover:bg-rose-400">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
